import { EntityAudit } from "@modernary/mui/components/audit";
import { EntityDocuments } from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView, type RouterOptions } from "vue-router";

// Troubleshoot your routes using this - https://paths.esm.dev

const names = {
  vacantLandInspections: "VacantLandInspection.VacantLandInspections",
  list: "VacantLandInspection.VacantLandInspectionList",
  new: "VacantLandInspection.VacantLandInspectionNew",
  base: "VacantLandInspection.VacantLandInspectionBase",
  view: "VacantLandInspection.VacantLandInspectionView",
  edit: "VacantLandInspection.VacantLandInspectionEdit",
  complete: "VacantLandInspection.VacantLandInspectionComplete",
  recomplete: "VacantLandInspection.VacantLandInspectionRecomplete",
  approve: "VacantLandInspection.VacantLandInspectionApprove",
  reject: "VacantLandInspection.VacantLandInspectionReject",
  processMail:  "VacantLandInspection.VacantLandInspectionProcessMail",
  documents: "VacantLandInspection.VacantLandInspectionDocuments",
  documentsEdit: "VacantLandInspection.VacantLandInspectionDocumentsEdit",
  documentsNew: "VacantLandInspection.VacantLandInspectionDocumentsNew",
  inspectionNotes: "VacantLandInspection.VacantLandInspectionNotes",
  noteEdit: "VacantLandInspection.VacantLandInspectionNoteEdit",
  noteNew: "VacantLandInspection.VacantLandInspectionNoteNew",
  audit: "VacantLandInspection.VacantLandInspectionAudit",
};

export { names as vacantLandInspectionRouteNames };

const vacantLandInspectionRoutes: RouterOptions["routes"] = [
  {
    path: "/vacantLandInspection",
    name: names.vacantLandInspections,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: {
          title: "Vacant Land Inspections",
          lookupGroups: ["ProgramType", "InspectionStatus", "InspectionType", "InspectionComplianceAction"],
        },
        component: () => import("@/views/vacantLandInspections/VacantLandInspectionList.vue"),
      },
      {
        path: "new/:vacantLandParcelId(\\d+)?",
        component: () => import("@/views/vacantLandInspections/VacantLandInspectionBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "New Vacant Land Inspection", isNew: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/vacantLandInspections/VacantLandInspectionBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Vacant Land Inspection View" },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Vacant Land Inspection Edit", isForm: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionEdit.vue"),
          },
          {
            path: "complete",
            name: names.complete,
            meta: { title: "Complete Inspection", isForm: true },
            component: () => import("@/views/vacantLandInspections/CompleteInspection.vue"),
          },
          {
            path: "recomplete",
            name: names.recomplete,
            meta: { title: "Recomplete Inspection", isForm: true },
            component: () => import("@/views/vacantLandInspections/RecompleteInspection.vue"),
          },
          {
            path: "approve",
            name: names.approve,
            meta: { title: "Approve Inspection", isForm: true },
            component: () => import("@/views/vacantLandInspections/ApproveInspection.vue"),
          },
          {
            path: "reject",
            name: names.reject,
            meta: { title: "Reject Inspection", isForm: true },
            component: () => import("@/views/vacantLandInspections/RejectInspection.vue"),
          },
          {
            path: "process",
            name: names.processMail,
            meta: { title: "Process/Mail Inspection", isForm: true },
            component: () => import("@/views/vacantLandInspections/ProcessMailInspection.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Vacant Land Inspection Documents" },
            component: {
              render: () => (
                <EntityDocuments
                  idField="inspectionId"
                  routeNamePrefix="VacantLandInspection.VacantLandInspectionDocuments"
                />
              ),
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentsEdit,
            meta: { title: "Edit Vacant Land Inspection Document", isForm: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionDocument.vue"),
          },
          {
            path: "documents/new",
            name: names.documentsNew,
            meta: { title: "Create Vacant Land Inspection Documents", isForm: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionDocuments.vue"),
          },
          {
            path: "notes",
            name: names.inspectionNotes,
            meta: { title: "Vacant Land Inspection Notes" },
            component: {
              render: () => (
                <EntityNotes idField="inspectionId" routeNamePrefix="VacantLandInspection.VacantLandInspectionNote" />
              ),
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Edit Vacant Land Inspection Note", isForm: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Create Vacant Land Inspection Note", isForm: true },
            component: () => import("@/views/vacantLandInspections/VacantLandInspectionNote.vue"),
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Vacant Land Inspection Audit Activity" },
            component: {
              render: () => <EntityAudit entityType="inspection" />,
            },
          },
        ],
      },
    ],
  },
];
export default vacantLandInspectionRoutes;
